import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import { getOptionSetArchetypeOrSelf } from "./getOptionSetArchetypeOrSelf";
import { isIntersectingOptionSet } from "./optionChoicesTools";
import { optionSetDefaultOptionId } from "./optionSetBaseUtils";
import { optionSetName, showroomOptionName } from "./optionSetTools";

/**
 * Sorts the option sets within a table
 *
 * @param optionSets Table of option sets
 * @param filterFn Function to filter by
 *
 * @returns List of sorted option set IDs
 */
export function sortOptionSetIds(
  optionSets: Table<DocOptionSet>,
  filterFn: (key: string) => boolean = () => true,
): string[] {
  return Object.keys(optionSets)
    .filter(filterFn)
    .sort((a, b) => optionSetName(optionSets[a], a).localeCompare(optionSetName(optionSets[b], b)));
}

/**
 * Sorts the options within an option set
 *
 * @param optionSet The DocOptionSet to sort
 * @param optionSets The table of option sets to use for resolving default values of instance option sets
 * @param bySortIndex Whether or not to use sortIndex to sort
 * @param byPrice Whether or not to use price to sort
 *
 * @returns List of sorted option IDs
 */
export function sortOptionIds(
  optionSet: DocOptionSet,
  optionSets: Table<DocOptionSet>,
  bySortIndex: boolean = true,
  byPrice: boolean = false,
): string[] {
  const currentOptions = optionSet.options;

  if (!currentOptions) {
    return [];
  }

  const defaultValue = optionSetDefaultOptionId(optionSet, optionSets);

  const sortedOptionIds = Object.keys(currentOptions).sort((a, b) => {
    const optionIdsA = currentOptions[a];
    const optionIdsB = currentOptions[b];

    if (bySortIndex && optionIdsA.sortIndex !== undefined && optionIdsB.sortIndex !== undefined) {
      return optionIdsA.sortIndex - optionIdsB.sortIndex;
    } else if (byPrice && defaultValue === a) {
      return -1;
    } else if (byPrice && optionIdsA.price) {
      return 1;
    } else if (byPrice && optionIdsA.price !== undefined && optionIdsB.price !== undefined) {
      return optionIdsA.price < optionIdsB.price ? -1 : 0;
    } else if (optionIdsA.name && optionIdsB.name) {
      return optionIdsA.name.localeCompare(optionIdsB.name);
    }
    return 0;
  });

  return sortedOptionIds;
}

/**
 * Walks an option set's options in `sortOptionIds` order and returns the first one that is
 * not disabled and not excluded by `isExcluded`. Returns `null` when no candidate qualifies.
 *
 * Used by callers that need a sensible default when no choice or default value resolves.
 */
export function findFirstEnabledOptionId(
  optionSet: DocOptionSet,
  optionSets: Table<DocOptionSet>,
  isExcluded?: (optionId: string) => boolean,
): string | null {
  for (const id of sortOptionIds(optionSet, optionSets)) {
    const option = optionSet.options[id];
    if (!option || option.disabled) {
      continue;
    }
    if (isExcluded && isExcluded(id)) {
      continue;
    }
    return id;
  }
  return null;
}

/**
 * Sorts the option set IDs within a table by:
 *  * option type (changed, base), then
 *  * numerically by builder option id, then
 *  * alphabetically by option choice display name
 *
 * @returns List of sorted option set IDs
 */
export function sortOptionSetIdsByBuilderOptionCode(
  activeOptions: Table<Pick<DocOption, "id" | "name" | "builderOptionCode">>,
  definitions: OptionDefinitions,
): string[] {
  const getBuilderOptionCode = (optionSetId: string) => activeOptions[optionSetId]?.builderOptionCode ?? "";

  return Object.keys(activeOptions)
    .filter((optionSetId) => getOptionSetArchetypeOrSelf(definitions, optionSetId) !== undefined)
    .sort((a, b) => {
      // list explicit option choices first, then base options.
      const set1 = getOptionSetArchetypeOrSelf(definitions, a);
      const set2 = getOptionSetArchetypeOrSelf(definitions, b);
      if (!set1 || !set2) {
        return 0;
      }
      const choice1 = activeOptions[a];
      const choice2 = activeOptions[b];
      const default1 = optionSetDefaultOptionId(set1, definitions.optionSets);
      const default2 = optionSetDefaultOptionId(set2, definitions.optionSets);
      const isBase1 = default1 ? choice1.id === set1.options[default1]?.id : false;
      const isBase2 = default2 ? choice2.id === set2.options[default2]?.id : false;
      const isIntersecting1 = isIntersectingOptionSet(set1);
      const isIntersecting2 = isIntersectingOptionSet(set2);

      if (isBase1 === isBase2 && isIntersecting1 === isIntersecting2) {
        // secondary sort by builder option id
        const builderOptionCode1 = getBuilderOptionCode(a);
        const builderOptionCode2 = getBuilderOptionCode(b);

        if (builderOptionCode1 === builderOptionCode2) {
          return showroomOptionName(choice1, set1).localeCompare(showroomOptionName(choice2, set2));
        }
        if (!builderOptionCode1) {
          return 1;
        }
        if (!builderOptionCode2) {
          return -1;
        }

        return builderOptionCode1.localeCompare(builderOptionCode2, undefined, { numeric: true });
      }
      if (isIntersecting1) {
        return 1;
      }
      if (isBase1) {
        return 1;
      }
      return -1;
    });
}
